import type { Dispatch, SetStateAction } from 'react';
import React, { useEffect, useState } from 'react';
import type {
  CustomerRegisteryEnabledDto,
  OrdrServiceSearchHitDto,
  OrdrServiceSearchResponseDto,
  UnintegratedPaymentFields,
} from '../../generated-api';

import type { DebouncedState } from 'use-debounce';
import useIntl from '../hooks/useIntl';
import { ComboBox, TextInput } from '@ordr-as/umami-react/form';

interface InvoiceFieldProps {
  setSearchText: DebouncedState<(value: string | undefined) => void>;
  setDynamicFieldValues: Dispatch<SetStateAction<Record<string, string>>>;
  unintegratedField: UnintegratedPaymentFields;
  customerRegistryEnabledDto: CustomerRegisteryEnabledDto | undefined;
  customerRegistry: OrdrServiceSearchResponseDto | undefined;
  dyamicFieldValues: Record<string, string>;
}

const InvoiceField = (props: InvoiceFieldProps) => {
  const {
    unintegratedField,
    customerRegistry,
    customerRegistryEnabledDto,
    setDynamicFieldValues,
    setSearchText,
    dyamicFieldValues,
  } = props;

  const intl = useIntl();
  const [isCustomerRegistryInitiallyLoaded, setIsCustomerRegistryInitiallyLoaded] = useState(false);

  useEffect(() => {
    if (customerRegistry?.hits !== undefined) {
      setIsCustomerRegistryInitiallyLoaded(true);
    }
  }, [customerRegistry, setIsCustomerRegistryInitiallyLoaded]);

  const items =
    customerRegistry?.hits.map((customer) => ({
      additionalData: customer,
      info: customer.mail,
      subtitle: `Org no: ${customer.businessRegisterId}. Cust. no: ${customer.sourceNo}`,
      title: customer.name,
    })) ?? [];

  const initialSelectedItem = items.find(
    (item) => item.additionalData.externalReference === dyamicFieldValues['externalReference']
  );

  const updateDynamicFieldValues = (newValue: string, key: string) => {
    setDynamicFieldValues({
      ...dyamicFieldValues,
      [key]: newValue,
    });
  };

  if (customerRegistryEnabledDto?.enabled && unintegratedField.type === 'TEXT_COMBO_BOX') {
    if (!isCustomerRegistryInitiallyLoaded) {
      // Wait for customer registry to load, then set the initialSelectedItem before rendering the combo box.
      return null;
    }

    return (
      <ComboBox<OrdrServiceSearchHitDto>
        className="mb-4"
        label={unintegratedField.displayLabel}
        description={intl.formatMessage({ id: 'checkout.invoiceField.description' })}
        placeholder={intl.formatMessage({ id: 'checkout.invoiceField.placeholder' })}
        required={unintegratedField.required}
        key={unintegratedField.displayLabel}
        initialSelectedItem={initialSelectedItem}
        items={items}
        onSelectItem={(item) => {
          if (item) {
            updateDynamicFieldValues(item.additionalData.externalReference, unintegratedField.name);
            return;
          }
          updateDynamicFieldValues('', unintegratedField.name);
        }}
        onInputValueChange={(inputValue) => {
          if (inputValue !== undefined) {
            setSearchText(inputValue);
          }
        }}
      />
    );
  }
  return (
    <TextInput
      label={unintegratedField.displayLabel}
      className="mb-4"
      required={unintegratedField.required}
      fullWidth
      key={unintegratedField.displayLabel}
      value={dyamicFieldValues[unintegratedField.name] ?? ''}
      onChange={(e) => {
        setDynamicFieldValues({ ...dyamicFieldValues, [unintegratedField.name]: e.trimStart() });
      }}
    />
  );
};

export default InvoiceField;
